'use client';

import Image from 'next/image';
import Link from 'next/link';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaStar } from 'react-icons/fa6';
import { GoArrowRight, GoArrowLeft } from 'react-icons/go';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import type { Review } from '@eliadmin/core/src/api/general/general-api';

export function Reviews({
  reviews,
  title,
  description,
}: {
  reviews: Review[];
  title: string;
  description: string;
}) {
  const isDesktop = useBreakpoint('md');

  const options = {
    align: isDesktop ? 'start' : ('center' as const),
  };

  return (
    <section
      className={cn(
        'custom-section',
        'px-0 pb-[0px] pt-[60px] sm:pt-[80px] md:pb-[40px] lg:pb-[60px] lg:pt-[120px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-[30px] md:flex-row md:items-stretch lg:gap-x-6',
        )}
      >
        <div className="relative z-10 flex w-full max-w-[384px] flex-col items-start justify-between gap-[30px] bg-white pl-6 pr-6 md:min-w-[300px] md:pr-0 xl:pl-0">
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <Heading title={title} />
            <Paragraph className="w-full" size="lg" color="secondary">
              {description}
            </Paragraph>
            <div className="flex w-full items-center justify-start gap-6">
              <div className="flex">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Image
                    key={i}
                    src={`/images/avatars/${i + 1}.png`}
                    alt={i.toString()}
                    width={44}
                    height={44}
                    className={cn('object-cover object-center', {
                      '-ml-[14px]': i !== 0,
                    })}
                  />
                ))}
              </div>
              <div className="flex flex-col items-start justify-start gap-1">
                <div className="flex items-center justify-start gap-[10px]">
                  <FaStar size={24} className="text-[#EDB000]" />
                  <p className="text-sm font-bold leading-[100%] tracking-tight text-secondary sm:text-base sm:leading-[100%] md:text-lg md:leading-[100%] lg:text-xl lg:leading-[100%] xl:text-2xl xl:leading-[100%]">
                    4.9/5
                  </p>
                </div>
                <Paragraph color="secondary">z 200+ recenzií</Paragraph>
              </div>
            </div>
          </div>
          {/* <Button asChild variant={'outline'} className="w-full sm:w-fit">
            <Link href={'#'} target="_blank" prefetch={false}>
              <ButtonIcon alt="Google" url="/images/icons/google.svg" />
              Všetky recenzie
            </Link>
          </Button> */}
        </div>
        <div
          style={{ clipPath: 'inset(-100vw 0 0 0)' }}
          className="flex w-full flex-col items-start justify-start gap-12"
        >
          <Carousel
            options={options}
            className={{
              wrapper: 'w-full overflow-visible pb-[60px] md:pb-0',
              main: 'w-full overflow-visible md:pl-8',
              slide: 'w-full max-w-[270px] sm:max-w-[290px] md:max-w-[330px] lg:max-w-[360px]',
              container: 'w-full gap-6 overflow-visible',
            }}
            slides={reviews.map(review => (
              <ReviewCard review={review} key={review.name} />
            ))}
          />
          <CarouselControls
            className={{
              main: 'hidden gap-3 sm:flex',
              button:
                'max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px] rounded-full bg-primary !p-0 text-secondary hover:bg-primary/75',
            }}
            nextIcon={<GoArrowRight size={20} className="text-secondary" />}
            prevIcon={<GoArrowLeft size={20} className="text-secondary" />}
          />
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: Review }) {
  return (
    <div
      className={cn(
        'flex h-[380px] w-full flex-col items-start justify-between rounded-[20px] border p-6',
        'ea-review-card',
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-6">
        <div className="flex items-center justify-start gap-6">
          <div className="flex items-center justify-start gap-[10px]">
            <div className="flex items-center justify-start gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <FaStar size={20} key={i} className="text-[#EDB000]" />
              ))}
            </div>
            <p className="text-lg font-bold tracking-tight text-secondary">5.0</p>
          </div>
        </div>
        <HtmlRendererRaw
          className={cn('line-clamp-5 w-full', 'ea-review-card__review')}
          html={review.review}
        />
      </div>
      <div className="flex w-full items-end justify-between">
        <div className="flex flex-col items-start justify-start gap-1">
          <p className="text-base font-bold text-secondary">{review.name}</p>
          {/* <p className="text-sm font-normal text-secondary">Bratislava, SK</p> */}
        </div>
        <Image
          src={'/images/icons/google.svg'}
          alt="Google"
          width={24}
          height={24}
          className="shrink-0 object-contain"
        />
      </div>
    </div>
  );
}
